import { FieldSpec } from './trace-redactor';

/**
 * Taxonomia do funil do pedido — fonte ÚNICA dos steps.
 *
 * Convenção sobre configuração: `step` é `<Source><Verb>` PascalCase, e todo
 * step emitido precisa existir aqui. Um step fora desta tabela não tem
 * allowlist, e sem allowlist o redator descarta o payload inteiro
 * (default-deny) — errar o nome do step não vaza dado, só acende `_dropped`.
 *
 * Forma dos eventos de FUNÇÃO: um evento por função, com `in` e `out` juntos,
 * emitido DEPOIS do retorno:
 *
 *     data = { in: {...}, out: {...}, ms, err }
 *
 * Por que junto, e não `XxxRequested`/`XxxResolved`:
 *  - metade do volume;
 *  - o gerador de golden da Etapa 2 lê UMA linha e tem um caso de teste
 *    completo — com dois eventos seria preciso um join, e joins perdem metades;
 *  - função que lança emite com `out: null, err: '<classe>'`, então a ausência
 *    de resultado vira sinal em vez de silêncio.
 *
 * Eventos de TELA não são funções: só `out` (o estado que a tela mostrou).
 *
 * ADITIVO por padrão: só adicionar steps/campos. Renomear ou remover quebra
 * query histórica e alarme — deprecar via campo paralelo.
 */

export interface StepSpec {
  /** Allowlist do payload de entrada da função. */
  in?: FieldSpec;
  /** Allowlist do payload de saída da função (ou do estado da tela). */
  out?: FieldSpec;
  /**
   * Fração de eventos emitidos (0..1), aplicada por evento SOBRE a coorte de
   * device. Default 1. Só para steps que disparam a cada clique — o funil em si
   * nunca é amostrado por evento (ver trace-utils.inSample).
   */
  sample?: number;
  /**
   * Chave de amostragem COMPARTILHADA entre steps que se dividem (numerador /
   * denominador). Default: o próprio nome do step.
   *
   * Existe porque a amostragem por evento é determinística por
   * `deviceId + ':' + chave`: dois steps amostrados a 20% CADA UM com a sua
   * própria chave sorteiam CONJUNTOS DIFERENTES de devices, e a razão entre
   * eles deixa de significar qualquer coisa — o numerador vem de uma população
   * e o denominador de outra. Declarando a mesma `sampleKey`, o device entra ou
   * sai dos dois juntos e a razão sobrevive à amostragem.
   *
   * Quem usa uma chave compartilhada tem de declarar a MESMA `sample` em todos
   * os steps do grupo (o spec da taxonomia reprova a divergência).
   */
  sampleKey?: string;
}

/**
 * Item do pedido — campos conforme `OrderProductDTO` (shared/model/dto/OrderProduct.ts).
 * Estrutura e dinheiro preservados; texto livre vira forma.
 */
const ORDER_ITEM: FieldSpec = {
  id: 'raw',
  type: 'raw',
  name: 'raw',          // nome de PRODUTO não é PII, e é o que torna o golden legível
  qty: 'num',
  featured: 'num',
  price: 'money',
  subtotal: 'money',
  points_needed: 'num',
  notes: 'str',         // texto livre do cliente ("apto 42, ligar 9999") — É PII
  // ORDER_ITEM é COMPARTILHADO por dois eventos com formatos de item DIFERENTES:
  //  - ClientCartViewed manda o RESUMO do carrinho: flavors/comboItems como
  //    presença (bool) + extras_count/flavors_count (contagens);
  //  - ClientOrderPayloadBuilt (golden mestre) manda o item SANITIZADO EXATO que
  //    vai ao backend, com a ESTRUTURA (extras[], crust, dough, associationID,
  //    discount, addition).
  // A allowlist é a UNIÃO das duas: cada evento manda só o seu subconjunto e o
  // que não vem não é capturado (sem drop). Faltava o lado do payload — era ~30k
  // `_dropped`/dia em ClientOrderPayloadBuilt (1 por item simples = `extras`; ~6
  // por pizza), a MESMA classe de erro que o `_dropped` já pegou em ORDER_USER
  // (zipcode→CEP). Tudo id/contagem/dinheiro, zero PII.
  flavors: 'bool',
  comboItems: 'bool',
  extras_count: 'num',
  flavors_count: 'num',
  extras: { $each: { id: 'raw', qty: 'num' } },
  associationID: 'raw',
  discount: 'money',
  addition: 'money',
  crust: { id: 'raw', sizeID: 'raw' },
  dough: { id: 'raw', sizeID: 'raw' },
};

/**
 * Usuário no payload — campos conforme `OrderDTO.user`.
 *
 * Os nomes vêm do DTO real, não de suposição: a primeira versão desta allowlist
 * usava `zipcode`/`complement`/`carPlate` e o E2E acusou `_dropped=19`, porque
 * os campos de verdade são `CEP`/`complemento`/`car_plate`. Foi o próprio
 * `_dropped` que apontou o erro.
 */
const ORDER_USER: FieldSpec = {
  id: 'raw',
  name: 'str',
  email: 'hash8',       // hash permite dedup/GROUP BY sem revelar
  cpf: 'redacted',
  phone: 'str',
  birthday: 'str',
  address: 'str',
  number: 'str',
  complemento: 'str',
  address_ref: 'str',
  zone: 'str',
  region: 'raw',
  city: 'raw',          // grosso o bastante, e é chave de zona/frete
  country: 'raw',
  CEP: 'str',
  coordinates: { latitude: 'coord3', longitude: 'coord3' },
  car_plate: 'redacted',    // identifica um veículo
  car_model_color: 'str',
  one_signal_id: 'hash8',   // id de push: identifica o dispositivo
  debugMode: 'num',
};

/**
 * `OrderDTO.details` — o bloco que carrega TODO o dinheiro do pedido.
 * Nomes conforme o DTO (inclusive os com typo de origem: `paymenthMethod*`).
 */
const ORDER_DETAILS: FieldSpec = {
  // dinheiro — cru, é o objeto do teste
  total: 'money',
  grandTotal: 'money',
  deliveryFee: 'money',
  deliveryFeeDiscount: 'money',
  discountValue: 'money',
  cashbackSpent: 'money',
  operatedValue: 'money',
  changeValue: 'money',
  paymenthMethodValue: 'num',
  // classificação
  deliveryFeeID: 'raw',
  discountType: 'raw',
  discountID: 'raw',
  deliveryType: 'raw',
  paymentType: 'raw',
  cardType: 'raw',
  cardMethod: 'raw',
  paymenthMethodOperation: 'raw',
  paymenthMethodValueType: 'raw',
  source: 'raw',
  status: 'raw',
  externalSource: 'raw',
  campaign: 'raw',
  carTakeout: 'bool',
  needChange: 'bool',
  isCartRecover: 'bool',
  isTable: 'num',
  schedule: 'bool',
  scheduleTo: 'str',
  table: 'str',
  linkUUID: 'str',
  guestCheckPad: 'bool',    // só presença: conteúdo desconhecido não sai
  // credenciais de pagamento: nunca saem do dispositivo
  creditCardCustomer: 'redacted',
  creditCardToken: 'redacted',
  creditCardUUID: 'redacted',
  onlinePaymentReference: 'redacted',
  tunaSessionId: 'redacted',
  coordinates: { latitude: 'coord3', longitude: 'coord3' },
};

export const ORDER_FLOW_STEPS: { [step: string]: StepSpec } = {
  // ── Menu ────────────────────────────────────────────────────────────────
  ClientMenuViewed: {
    out: {
      products_count: 'num', categories_count: 'num',
      cashier_is_open: 'bool', is_closed: 'bool',
      layout: 'raw', menu_channel: 'raw', has_repeat_uuid: 'bool',
    },
  },
  ClientMenuGateEvaluated: {
    in: {
      delivery_type_saved: 'raw', is_table: 'bool', table_uuid_present: 'bool',
      cashier_is_open: 'bool', has_pos: 'bool', digital_menu: 'bool',
    },
    out: { can_use: 'bool', redirected_to: 'raw' },
  },

  // ── Foto ampliada do item / botão "Quero esse" ──────────────────────────
  /**
   * DENOMINADOR do botão "Quero esse": a foto ampliada foi aberta e o botão
   * FOI RENDERIZADO.
   *
   * `action_rendered` é lido do DOM (o ViewChild do botão existe?), não da
   * intenção do opener. É a diferença entre medir "pedi o botão" e "o botão
   * está na tela": um erro de template/CSS que suma com ele apareceria aqui
   * como denominador que despenca, em vez de virar um numerador misteriosamente
   * baixo. É também o CONTROLE POSITIVO do instrumento — zero clique com
   * denominador vivo é "ninguém clica"; zero clique com denominador zero é
   * "a telemetria (ou o botão) morreu".
   *
   * Emitido por TODA abertura do visualizador, inclusive as das telas de
   * pizza/combo/complementos, que não pedem botão (`action_rendered: false`) —
   * é o que dá a linha de base de "quanto a foto ampliada é usada" contra a
   * qual a adoção do botão se lê.
   */
  ClientItemPhotoOpened: {
    sampleKey: 'item-photo',
    in: { item_id: 'raw', item_type: 'raw', price: 'money', opener: 'raw' },
    out: {
      action_rendered: 'bool',
      action_disabled: 'bool',
      light_action: 'bool',
      has_description: 'bool',
    },
  },
  /** NUMERADOR: o consumidor tocou no "Quero esse". */
  ClientItemPhotoActionClicked: {
    sampleKey: 'item-photo',
    // Mesma identificação nos três eventos da foto: o emitter manda um bloco só
    // (viewer-modal.trackingIn) e allowlist que divergisse acenderia `_dropped`.
    in: { item_id: 'raw', item_type: 'raw', price: 'money', opener: 'raw' },
    out: { ms_open: 'num', zoom_used: 'bool', light_action: 'bool' },
  },
  /**
   * Desfecho da foto ampliada — `outcome: 'select' | 'close'`.
   *
   * COMPLEMENTAR, nunca o denominador: sai do `ngOnDestroy`, que não roda se a
   * aba morrer com o modal aberto. A taxa de uso do botão se calcula sempre
   * contra `ClientItemPhotoOpened`, que é emitido na abertura e não depende de
   * o consumidor chegar ao fim de nada.
   */
  ClientItemPhotoClosed: {
    sampleKey: 'item-photo',
    in: { item_id: 'raw', item_type: 'raw', price: 'money', opener: 'raw' },
    out: {
      outcome: 'raw', action_rendered: 'bool', action_disabled: 'bool',
      ms_open: 'num', zoom_used: 'bool',
    },
  },

  // ── Produto ─────────────────────────────────────────────────────────────
  ClientProductOpened: {
    // `opened_via` ('card' | 'photo_action') é o que liga o botão ao RESTO do
    // funil: com ele, `ClientProductAddedToCart`/`ClientOrderSubmitted` do mesmo
    // correlation_id passam a ser atribuíveis à origem da abertura, e a pergunta
    // "quem entra pelo botão compra mais?" vira uma query só (ver
    // TELEMETRIA-QUERO-ESSE.md).
    in: {
      item_id: 'raw', item_type: 'raw', price: 'money', extras_count: 'num',
      opened_via: 'raw',
    },
    out: { target_page: 'raw', is_combo: 'bool' },
  },
  ClientProductPriceCalculated: {
    in: {
      base_price: 'money',
      selected_extras: { $each: { id: 'raw', qty: 'num', price: 'money' } },
    },
    out: { total: 'money' },
  },
  ClientPizzaPriceCalculated: {
    // Dispara a cada clique de sabor/borda: amostrado por device p/ não virar
    // firehose de UI (como ClientCartItemQuantityChanged). Legado × NEP mesmo step.
    sample: 0.2,
    in: {
      // crust/dough/base/slice PRICE: sem eles o golden de calculatePizzaTotal é
      // infatiável (a função soma crust.price+dough.price+basePrice e, no
      // incremental, slicePrice*qty — nada disso derivável só dos ids).
      size_id: 'raw', crust_id: 'raw', crust_price: 'money',
      dough_id: 'raw', dough_price: 'money', base_price: 'money', pricing_rule: 'raw',
      flavors: { $each: { id: 'raw', quantity: 'num', price: 'money', slice_price: 'money' } },
      additional_toppings: { $each: { id: 'raw', price: 'money' } },
      extras: { $each: { id: 'raw', qty: 'num', price: 'money' } },
    },
    out: { total: 'money', average: 'money' },
  },
  /**
   * O estado de um combo anterior foi descartado ao abrir outro combo (MP-539).
   *
   * Mede a EXPOSIÇÃO ao cenário do bug — quantas vezes alguém abandona um combo e
   * abre outro —, não o dano. Isso é deliberado, e é o que torna o funil legível:
   *
   *   exposição (aqui)  →  escape (combo_incomplete_blocked_locally)  →  falha (400)
   *
   * Se a correção funcionar 100%, este número NÃO vai a zero: ele é o denominador,
   * a prova de que a prevenção está agindo. Quem tem de ir a zero são os outros
   * dois. Um instrumento que zerasse junto não distinguiria "curado" de "ninguém
   * mais abre dois combos".
   */
  ClientComboStateDiscarded: {
    in: { previous_combo_id: 'raw', opened_combo_id: 'raw' },
    out: {
      had_items: 'bool', items_count: 'num',
      size_index: 'num', sizes_count: 'num',
    },
  },
  ClientProductAddedToCart: {
    in: { product_id: 'raw', type: 'raw', qty: 'num', total_price: 'money' },
    out: { cart_items: 'num', cart_total: 'money' },
  },

  // ── Carrinho ────────────────────────────────────────────────────────────
  ClientCartViewed: {
    out: {
      items: { $each: ORDER_ITEM },
      order_level_extras: 'money', total: 'money', layout: 'raw',
    },
  },
  ClientCartItemQuantityChanged: {
    // Dispara a cada clique de +/-: amostrado para não virar firehose de UI.
    sample: 0.2,
    in: { index: 'num', id: 'raw', qty_before: 'num', op: 'raw' },
    out: { qty_after: 'num', item_subtotal: 'money', cart_total: 'money', removed: 'bool' },
  },
  ClientCartProceeded: {
    in: { items_count: 'num', total: 'money', delivery_type: 'raw' },
    out: { blocked: 'bool', reason: 'raw' },
  },
  ClientTableOrderSubmitted: {
    in: { table: 'str', total: 'money', items_count: 'num' },
    out: { order_uuid: 'raw', order_id: 'raw', table_service_fee: 'money', err: 'raw' },
  },

  // ── Entrega ─────────────────────────────────────────────────────────────
  ClientDeliveryTypeChanged: {
    in: { from: 'raw', to: 'raw' },
    out: { delivery_fee: 'money', total: 'money', next_enabled: 'bool' },
  },
  ClientDeliveryFeeCalculated: {
    in: {
      address_flow: 'raw', fee_method: 'raw',
      street: 'str', number: 'str', zone: 'str', city: 'raw',
      lat: 'coord3', lng: 'coord3',
    },
    out: {
      delivery_fee: 'money', delivery_fee_id: 'raw', distance: 'num',
      found: 'bool', delivery: 'bool', min_total: 'money',
    },
  },
  ClientDeliveryFeeDiscountApplied: {
    in: { total: 'money', delivery_fee: 'money', discount_type_configured: 'raw' },
    out: { discount_value: 'money', discount_type: 'raw' },
  },
  ClientDeliveryTotalComputed: {
    in: {
      cart_total: 'money', delivery_fee: 'money',
      delivery_fee_discount: 'money', discount_type: 'raw',
      // delivery_type/has_street/table_service_fee: sem eles o ramo de
      // computeTotalValue não é derivável (o golden precisava inferi-lo do out, e
      // o ramo `table` era invisível).
      delivery_type: 'raw', has_street: 'bool', table_service_fee: 'money',
    },
    out: { total: 'money' },
  },
  ClientCheckoutValidated: {
    in: {
      delivery_type: 'raw', has_address: 'bool', has_fee_id: 'bool',
      user_fields_present: {
        name: 'bool', phone: 'bool', email: 'bool', cpf: 'bool',
        birthday: 'bool', number: 'bool', city: 'bool', carPlate: 'bool',
      },
    },
    out: { enabled: 'bool', failing: { $each: 'raw' } },
  },
  ClientAddressModalReturned: {
    in: { flow: 'raw' },
    out: {
      has_fee_id: 'bool', delivery_fee: 'money', discount_type: 'raw',
      street: 'str', city: 'raw', lat: 'coord3', lng: 'coord3',
    },
  },
  ClientDeliveryProceeded: {
    in: { delivery_type: 'raw', total: 'money' },
    out: { navigated: 'bool', fallback: 'raw' },
  },

  // ── Pagamento ───────────────────────────────────────────────────────────
  ClientPaymentViewed: {
    out: {
      order_total: 'money', shopping_cart_total: 'money', delivery_fee: 'money',
      methods_available: { $each: 'raw' }, layout: 'raw',
    },
  },
  ClientPaymentMethodsParsed: {
    in: { raw_count: 'num', sales_channel: 'raw', is_totem: 'bool', delivery_type: 'raw' },
    out: {
      online: { $each: 'raw' }, offline: { $each: 'raw' },
      has_pix_asaas: 'bool', has_pix_tuna: 'bool',
    },
  },
  // Evento de TELA: chegada pelo link compartilhável /c/<código> do Cupom v2
  // (CouponLinkGuard). Mede o uso do link do painel; o código sai hasheado.
  ClientCouponLinkOpened: {
    out: { code: 'hash8', valid_format: 'bool' },
  },
  ClientCouponChecked: {
    in: { code: 'hash8', cart_total: 'money' },
    out: {
      valid: 'bool', reason: 'raw',
      coupon: { type: 'raw', fix: 'money', percent: 'num', min_order: 'money' },
    },
  },
  ClientCouponApplied: {
    in: {
      cart_total: 'money',
      coupon: { type: 'raw', fix: 'money', percent: 'num', min_order: 'money' },
    },
    out: { discount_value: 'money', grand_total: 'money', rejected: 'bool', reason: 'raw' },
  },
  ClientCashbackApplied: {
    in: {
      available: 'money', order_total: 'money', delivery_fee: 'money',
      min_order: 'money', expires_in_days: 'num',
    },
    out: { cashback_spent: 'money', grand_total: 'money', rejected: 'bool', reason: 'raw' },
  },
  ClientDeliveryFeeParsed: {
    in: {
      delivery_type: 'raw', delivery_fee: 'money',
      delivery_fee_discount: 'money', discount_type: 'raw',
    },
    out: { fee: 'money' },
  },
  ClientPaymentOperationApplied: {
    in: {
      method: 'raw', order_total: 'money', coupon_value: 'money', cashback_spent: 'money',
      setting: { valueType: 'raw', value: 'num', operation: 'raw' },
    },
    out: { calculated_value: 'money', operation_name: 'raw', grand_total: 'money' },
  },
  /** O golden mestre: o payload que de fato vira pedido. */
  ClientOrderPayloadBuilt: {
    in: { chosen_methods: { $each: 'raw' } },
    out: {
      details: ORDER_DETAILS,
      user: ORDER_USER,
      orders: { $each: ORDER_ITEM },
      items_count: 'num',
    },
  },
  ClientOrderSubmitted: {
    in: {
      items_count: 'num', total: 'money', grand_total: 'money',
      payment_type: 'raw', delivery_type: 'raw',
    },
    out: { order_uuid: 'raw', order_id: 'raw', total_net_value: 'money', ms: 'num', err: 'raw' },
  },
  ClientOrderRejected: {
    in: { err_class: 'raw' },
    out: {
      is_stock_error: 'bool',
      action: 'raw',
      // `text` do generateMessage: é o que o cliente lê, e no erro de estoque
      // carrega nome de produto — por isso forma, não conteúdo.
      message_shown: 'str',
      // reason_code é um ENUM estável (não texto livre) → seguro como raw. É o que
      // torna a rejeição legível: hoje 100% dos eventos vinham como err='unknown'
      // (o backend responde corpo sem .status) e o motivo real ficava só no
      // message_shown redigido a str(N). Ver classifyRejectionReason em payment.page.ts.
      reason_code: 'raw',
    },
  },
  ClientOrderPayloadSanitized: {
    in: { items: { $each: { type: 'raw', keys_count: 'num' } } },
    out: { items: { $each: { type: 'raw', keys_count: 'num' } }, dropped_items: 'num' },
  },

  // ── Sucesso ─────────────────────────────────────────────────────────────
  ClientOrderConfirmed: {
    in: { order_uuid: 'raw', method: 'raw' },
    out: { status: 'raw', ms: 'num', err: 'raw' },
  },

  // ── Saúde do app (rollup 15-min → health_15m → alertas por fluxo) ───────
  // Emitidos via emitHealth(): gate por device quando o restaurante ainda não
  // carregou (erro de bootstrap não pode ser invisível) e transporte por
  // sendBeacon (sobrevive a unload/crash de navegação).
  ClientAppError: {
    out: {
      // Classe do erro (TypeError, HttpErrorResponse...) — enum de fato, e é a
      // dimensão (`dim`) do kind js_error no health_15m.
      name: 'raw',
      // Mensagem vira FORMA (str(N)): mensagem de erro carrega dado do usuário
      // com frequência (nome de produto no erro de estoque, endereço no de
      // frete). O hash8 permite agrupar ocorrências sem revelar o texto.
      message: 'str',
      message_hash: 'hash8',
      // Frames iniciais do stack: apontam para o BUNDLE (código estático),
      // não para dado de cliente — raw truncado no emitter (180 chars).
      stack_head: 'raw',
      // Rota da SPA (slug público da loja) — é o que localiza o erro na tela.
      route: 'raw',
      // Ocorrências desta assinatura na janela de throttle (1 device em loop
      // conta como N no payload, não como N eventos).
      count: 'num',
      // Veredito do ChunkLoadRecovery — só presente em ChunkLoadError:
      // 'reload' | 'cooldown' | 'max' | 'no_storage'. Constante do nosso
      // código (sem PII), e é o que torna a cura MENSURÁVEL: sem ele não dá
      // para saber quantos chunks quebrados viraram reload bem-sucedido.
      // Campo novo aqui NÃO exige migração de Glue — `data` é JSON livre.
      recovery: 'raw',

      // --- Diagnóstico de ChunkLoadError (só presente nesses erros) ---
      // Por que existe: sem estes campos as TRÊS causas de falha de chunk
      // chegam ao Athena como uma só, e elas pedem curas opostas. O runtime do
      // webpack já as distingue e carimba no objeto de erro — a telemetria é
      // que descartava (a `message`, único lugar onde a causa aparecia, é
      // redigida para `str(N)` logo acima).
      //
      // `error`|`timeout`|`missing` — enum do runtime do webpack. `missing`
      // significa que o script BAIXOU com sucesso e o chunk não registrou:
      // conteúdo errado com HTTP 200, não rede. E `error` NÃO é só rede: o
      // 410 vazio que a borda devolve para chunk podado também chega como
      // `error` (módulo ES recusa MIME `text/plain`) — medido 08/09/2026.
      chunk_type: 'raw',
      // URL do chunk que falhou. É caminho de BUNDLE (código estático), mesma
      // natureza do `stack_head` acima — sem dado de cliente.
      chunk_url: 'raw',
      // Condição de rede no instante da falha (`navigator.connection`).
      // Chromium-only: no WebKit vem ausente, e a ausência cruza com user_agent.
      net_type: 'raw',
      net_downlink: 'num',
      // O que a request realmente trouxe (PerformanceResourceTiming). É o que
      // separa "não chegou" (transfer 0) de "chegou a coisa errada" (corpo de
      // ~3KB onde o chunk tem ~218KB) sem depender do `chunk_type`.
      res_body_size: 'num',
      res_transfer_size: 'num',
      res_ms: 'num',
      // Origem do DOCUMENTO que pediu o chunk. `doc_from_sw` (o service worker
      // atendeu a navegação) + `doc_transfer_size = 0` (não passou pela rede)
      // provam que o app subiu de um index guardado em cache — a única
      // explicação para uma build de 10 dias pedir chunk, já que o index tem
      // `max-age` de 30 min. Sem estes campos a causa continua sendo dedução.
      sw_controlled: 'bool',
      doc_from_sw: 'bool',
      doc_transfer_size: 'num',
      // COMO o documento chegou (08/09/2026). O index é servido com `no-cache`
      // e os erros de build velha vinham de documento em cache sob worker
      // RECENTE — só histórico/bfcache ou aba restaurada reusam um documento
      // assim. `nav_type` é o enum do Navigation Timing (`navigate`|`reload`|
      // `back_forward`|`prerender`); `delivery_type` é `cache` quando o
      // Chromium serviu do cache HTTP; `page_age_s` separa aba viva há dias de
      // aba restaurada agora; `visible` diz se havia alguém olhando. Todos
      // constantes de API ou números — sem PII.
      nav_type: 'raw',
      delivery_type: 'raw',
      page_age_s: 'num',
      // ⚠️ CORREÇÃO de 10/09/2026 — os três acima são armadilha sem o campo
      // abaixo. Voltar do bfcache NÃO cria entry de navegação nova: a entry
      // fica congelada na carga original, então `nav_type`/`delivery_type`/
      // `doc_transfer_size` podem descrever uma navegação de dias atrás
      // enquanto o erro é de agora. `doc_restaurado` conta as voltas
      // (`pageshow.persisted`) e é o que diz se os outros valem: ausente ou 0
      // ⇒ descrevem a carga que está rodando; ≥ 1 ⇒ descrevem outra coisa.
      // E ele separa dois fenômenos que se confundiam: documento restaurado
      // do bfcache mantém o bundle VIVO (medido em bancada, Chrome 149), logo
      // não pode dar erro de BOOT — `doc_restaurado ≥ 1` num erro de chunk é
      // falha em rota lazy DEPOIS da volta, outra cura.
      doc_restaurado: 'num',
      // Tempo em que o documento ficou CONGELADO (monotônico × parede). É ele,
      // e não `page_age_s`, que separa "aba viva há dias" de "documento
      // restaurado agora" — restaurar preserva o `timeOrigin`, então a idade é
      // alta nos dois casos. Ausente em documento vivo (piso de 1 s contra
      // jitter de relógio).
      susp_ms: 'num',
      visible: 'bool',
      // QUAL worker é esse. `sw_controlled` só diz que HÁ um no comando — e
      // medido em 15/08/2026, 128 devices rodaram a build nova num dia e
      // erraram no boot com a antiga depois: carregar o bundle pela rede não
      // troca o worker registrado. O nome do cache do PWA carrega a release do
      // worker que instalou; o nome LEGADO (sem sufixo) é a assinatura do
      // worker anterior à Etapa 1, que nunca podou nada naquele device.
      // `sw_cache_n > 1` significa poda incompleta. Nomes de cache do nosso
      // próprio código (sem PII). Ver sw-cache-probe.ts.
      sw_cache_legacy: 'bool',
      sw_cache_release: 'raw',
      sw_cache_n: 'num',
      // App instalado na tela inicial quase nunca é fechado, então segura um
      // documento em cache por muito mais tempo: é coorte, não detalhe.
      display_mode: 'raw',
      // Retentativas gastas antes deste erro (`chunk-retry.ts`). Campo AUSENTE
      // significa que o patch de retentativa não está instalado — sem essa
      // distinção, "retentou 2× e falhou" e "nunca retentou" chegariam ao
      // Athena com a mesma cara e pediriam decisões opostas.
      retry_n: 'num',
    },
  },
  /**
   * O boot não chegou à primeira tela dentro do prazo. Emitido pelo
   * `boot-watchdog.ts`, que NÃO passa por aqui — ele roda fora da injeção de
   * dependência de propósito, porque o cenário que mede é o Angular não ter
   * subido. Esta entrada existe para o evento ser achável junto dos outros e
   * para documentar o payload; a garantia de que nada de cliente entra nele é
   * de construção (todo campo é constante ou número medido no próprio vigia).
   */
  ClientBootStalled: {
    out: {
      // A PLATAFORMA. Até 01/09/2026 este passo NÃO coletava user_agent, e por
      // isso os 328-471 devices/dia travados eram um agregado sem dono: não
      // dava para dizer se são o mesmo público do boot morto (medido 100% iOS
      // 15/Safari) ou outro completamente diferente — e essa diferença decide
      // se são uma frente ou duas. Mesmo `.slice(0, 200)` do
      // `OrderFlowTraceService` e da sonda inline, para as TRÊS fontes
      // cruzarem no Athena sem normalização.
      user_agent: 'raw',
      // 'cardapio' | 'bootstrap' | 'primeira-tela' | 'navegacao-pedida' — onde
      // parou. É o campo que transforma "travou" em diagnóstico: 'cardapio'
      // significa que o APP_INITIALIZER não voltou e o Angular nem montou.
      //
      // 'navegacao-pedida' entrou em 01/09/2026 e é a fronteira que faltava:
      // até então 'primeira-tela' somava DOIS defeitos com curas opostas —
      // "montou e nem chegou a pedir navegação" e "pediu o `navService.push` e
      // a promessa não assentou" (o candidato medido: 471 travados em 31/08,
      // dos quais só 14 emitiram `ClientAppError`; silêncio é a assinatura de
      // promessa que nunca assenta, uma que rejeitasse produziria erro).
      fase: 'raw',
      // Prazo que estourou, em ms. É a CONSTANTE (`PRAZO_VIGIA_MS`), não uma
      // medida — ver `ms_nav` logo abaixo, que é a medida.
      ms: 'num',
      // Tempo REAL de vida do documento (`performance.now()` no envio). Como
      // `ms` é constante, sozinho ele não separa "o app travou 20 s" de "o
      // vigia só foi armado aos 40 s porque o bundle demorou a chegar" — os
      // dois viram o mesmo evento. `ms_nav` separa, e é comparável com o `ms`
      // MEDIDO do `ClientBootAbandoned`. AUSENTE quando não há relógio ao
      // alcance: ausência é informação, um `0` inventado envenenaria a média
      // justamente na cauda que interessa.
      ms_nav: 'num',
      // Para onde a navegação de entrada ia. '/menu' travando é um defeito;
      // '/rate-order' ou '/shopping-cart' travando é outro (só o cliente
      // recorrente chega neles, e são os caminhos que esperam rede antes de
      // navegar). Sanitizado no vigia (`sanitizarRota`): sem query string, sem
      // fragmento, só segmentos de nome de rota (letras/hífen, sem dígito),
      // cortado em 40 — o `routeTo` pode ser o caminho da URL do cliente, e nos
      // fluxos track/rate-order/reset-password ele carrega TOKEN.
      //
      // A AUSÊNCIA do campo é o sinal de que a navegação nunca foi pedida
      // (travou antes do `push`); '(outra)' significa que foi informada e nada
      // sobreviveu à sanitização. Se `rota_alvo` for 0% E `fase` nunca vier
      // 'navegacao-pedida', é instrumento não ligado, não defeito ausente.
      rota_alvo: 'raw',
      sw_controlled: 'bool',
      doc_from_sw: 'bool',
      net_type: 'raw',
      display_mode: 'raw',
      // 'navigate' | 'reload' | 'back_forward' — um 'reload' aqui sugere que o
      // usuário já tentou resolver sozinho antes de desistir.
      nav_type: 'raw',
    },
  },
  /**
   * A navegação de ENTRADA do boot estourou o prazo (ou rejeitou) e o plano B
   * agiu. Emitido por `app.component.ts reportarPlanoBDoBoot()`, via
   * `emitHealth` — logo este passo PASSA pela allowlist daqui (ao contrário do
   * `ClientBootStalled` e da sonda inline), e campo não declarado abaixo é
   * descartado em silêncio.
   *
   * ⚠️ LEITURA DO PAINEL: a partir da build que introduziu isto, parte da série
   * `ClientBootStalled` na fase 'primeira-tela' MIGRA para cá — o plano B marca
   * o boot como pronto e desarma o vigia. Ela não caiu, mudou de nome. O número
   * comparável com o histórico é a SOMA das duas séries.
   *
   * Cobertura: `emitHealth` passa pelo gate de coorte de saúde — 100% dos
   * devices nas lojas curadas, `samplePct` nas demais. Para ~334 devices/dia
   * isso rende dezenas de eventos/dia fora da coorte: é PISO, não taxa.
   */
  ClientBootNavFallback: {
    out: {
      // 'prazo' (a promessa do `navService.push` não assentou em
      // `PRAZO_NAVEGACAO_BOOT_MS`) ou 'rejeicao' (`navigateByUrl` rejeitou —
      // chunk lazy que não baixou, guard/resolver que lançou). São causas
      // diferentes e pedem investigações diferentes; sem este campo as duas
      // chegariam ao Athena com a mesma cara.
      motivo: 'raw',
      // A MESMA string do `rota_alvo` do `ClientBootStalled`, de propósito: ela
      // vem do próprio vigia (`rotaDeBoot()`), não de uma sanitização local.
      // Duas normalizações parecidas — uma com barra inicial, outra sem —
      // dariam duas colunas que nunca casam, e o join entre os dois passos
      // sairia vazio sem ninguém notar.
      rota_alvo: 'raw',
      // Para onde o plano B mandou o cliente: '/menu' (o alvo não era o
      // cardápio) ou 'nenhum' (o alvo JÁ era o cardápio — re-navegar
      // enfileiraria uma segunda promessa pendurada em cima da primeira).
      // Separa recuperação ATIVA de recuperação passiva.
      destino: 'raw',
      // Prazo que estourou, em ms (`PRAZO_NAVEGACAO_BOOT_MS`). Mesmo papel do
      // `ms` do `ClientBootStalled`: mantém a série comparável se o prazo for
      // recalibrado — e ele deve encolher se a medição mostrar que quase todo
      // estouro é pendura de verdade, não lentidão.
      ms: 'num',
      // CLASSE do erro quando motivo='rejeicao' ('ChunkLoadError',
      // 'HttpErrorResponse', 'TypeError'), 'sem-nome' se o objeto não tiver
      // `name`, string VAZIA quando 'prazo'. É o campo que DISCRIMINA: chunk
      // que não baixou e resolver que falhou pedem investigações diferentes.
      // `raw` porque o nome vem do nosso runtime, é vocabulário fechado e não
      // tem como carregar dado do cliente.
      erro_classe: 'raw',
      // Mensagem do erro, cortada em 160 no emissor. Enviada SEMPRE, nunca
      // omitida: a ausência de mensagem é o que confirma a promessa pendurada.
      //
      // `str` — e NÃO `raw`, apesar de o conteúdo ser do nosso próprio runtime.
      // A tentação aqui é o precedente do `stack_head`, mas ele não se aplica:
      // um `HttpErrorResponse` vindo de resolver traz a URL PEDIDA na mensagem,
      // e deep link de `/track` e `/rate-order` carrega `?token=`. O token é do
      // cliente, e uma coluna que só às vezes o contém é pior que uma que nunca
      // contém — ninguém audita o que é raro.
      //
      // O par abaixo é o padrão do `ClientAppError`: o emissor manda a MESMA
      // string nos dois campos e a allowlist redige cada um de um jeito. O que
      // se perde é ler a mensagem; o que NÃO se perde é diagnóstico —
      // `erro_classe` diz a classe e `erro_hash` agrupa as ocorrências iguais.
      erro_msg: 'str',
      // fnv1a de 8 hex da mensagem: permite GROUP BY e "isto é a mesma falha de
      // ontem?" sem revelar o conteúdo. Mesmo papel do `message_hash` do
      // `ClientAppError`.
      erro_hash: 'hash8',
    },
  },

  /**
   * O id do aparelho foi GERADO nesta sessão (não lido do cache) — um evento
   * por device na vida, mais um por sessão nos devices cujo storage não
   * persiste. Ver `fingerprint-id.ts`.
   *
   * Por que existe: em 01–08/09/2026 o `import()` do fingerprintjs falhou em
   * ~74 devices/semana (Safari iOS, bloqueador de conteúdo) e, sem `catch`,
   * recarregava o cardápio via `ChunkLoadRecovery`. Agora cai num id
   * aleatório de mesmo formato. Este passo é a régua da troca: a razão
   * `aleatorio / (aleatorio + fingerprintjs)` diz quanto do parque está no
   * fallback, e `motivo` diz se é bloqueio (`import`) ou biblioteca quebrada
   * (`load`/`get`). O id em si NÃO viaja — é identificador de aparelho.
   */
  ClientFingerprintResolved: {
    out: {
      // 'fingerprintjs' | 'aleatorio' — enum do nosso código.
      origem: 'raw',
      // Por que caiu no aleatório: 'import' | 'load' | 'get' | 'vazio' |
      // 'prazo' | 'carregador'; string vazia quando não caiu. 'prazo' NÃO
      // grava (medido 08/09: 73 de 73 fallbacks eram prazo, 69 Android lentos
      // — lentidão não é bloqueio); 'tardio' = o fingerprintjs chegou depois
      // do prazo e foi gravado para as próximas sessões (segundo evento do
      // mesmo device, com `persistido=true`).
      motivo: 'raw',
      // `name` do erro que causou o fallback ('ChunkLoadError', 'TypeError'…),
      // vazio quando não houve. Vocabulário do runtime, sem dado de cliente.
      erro_classe: 'raw',
      // Duração da resolução em ms. Com `motivo='prazo'` é o próprio prazo.
      ms: 'num',
      // O registro ficou no storage? `false` = só em memória: o device vai
      // gerar outro id na próxima sessão (webview com cookies bloqueados).
      persistido: 'bool',
    },
  },
  /**
   * O `main.js` nunca executou — o cliente ficou no splash estático do
   * `index.html`, sem app e sem erro. Emitido pela sonda INLINE no fim do
   * `index.html`, que também não passa por aqui (nem poderia: ela existe
   * justamente para o cenário em que este arquivo não carregou).
   *
   * ⚠️ O PAYLOAD DELE VEM PLANO, NÃO DENTRO DE `out`. Quem cria o envelope
   * `{in, out}` é o `OrderFlowTraceService`, e nem esta sonda nem o
   * `boot-watchdog` passam por ele — de propósito, porque os dois medem o
   * Angular não ter subido. No Athena, ler com `$.campo`, nunca `$.out.campo`:
   * o caminho errado devolve `null` em tudo e parece instrumento quebrado.
   * A estrutura aninhada abaixo é só a forma que este arquivo declara campos.
   *
   * Esta entrada existe para o passo ser achável junto dos outros e para
   * documentar o payload. A garantia de que nada de cliente entra nele é de
   * construção: todo campo é constante ou medido na própria sonda.
   *
   * Distinção que dá sentido aos passos de boot:
   *   ClientBootNoScript     o bundle NÃO executou     (splash eterno, zero telemetria)
   *   ClientBootStalled      executou e travou depois  (o vigia sabe a fase)
   *   ClientBootNavFallback  travou na navegação de entrada E o plano B agiu
   *
   * Escalas MEDIDAS em 01/09/2026 — as duas primeiras não se substituem:
   * 3 a 11 devices/dia no boot morto contra 328 a 471 no boot travado.
   */
  ClientBootNoScript: {
    out: {
      // A LOJA, tirada da URL. O `restaurant_id` do envelope fica '0' porque o
      // restaurante de fato não carregou — mas o slug está no caminho o tempo
      // todo, e sem ele não há como responder ao lojista que diz "meus clientes
      // não abrem o link". Em 15/08/2026 a loja 2605 reclamou disso e havia 64
      // devices travados no dia, sem forma nenhuma de saber quantos eram dela.
      slug: 'raw',
      // A PLATAFORMA. Sem ela o passo não responde a pergunta que o motivou —
      // o MP-569 é um relato de iPhone e o erro de boot é ~86% Android, então
      // o agregado responderia pela maioria. Mesmo `.slice(0, 200)` do
      // `OrderFlowTraceService`, para as duas fontes cruzarem no Athena.
      user_agent: 'raw',
      // O `main.<hash>.js` que ESTE documento pediu. Um hash que não está mais
      // no bucket é a prova direta de documento fóssil — sem ele saberíamos que
      // o boot morreu, não por quê.
      main_pedido: 'raw',
      ms: 'num',
      // O campo decisivo: o documento veio do service worker? Se a maioria dos
      // boots mortos tiver `true`, a causa é o worker servindo um index velho
      // cujo `main.js` o S3 já podou.
      doc_from_sw: 'bool',
      sw_controlled: 'bool',
      net_type: 'raw',
      // Memória, testando a hipótese que sobra depois que rede e sintaxe
      // caíram: 1,44 MB de JS para compilar de uma vez, e webview embarcado tem
      // teto menor que o browser nativo do mesmo aparelho. `dev_mem` é o RAM do
      // APARELHO em GB (Chromium arredonda, teto 8); `heap_max`/`heap_uso` são o
      // teto e o uso de heap DESTE documento. Só existem em Chromium — e a
      // coorte de 08/09/2026 é 100% Android/Chromium, zero WKWebView; em iOS a
      // ausência dos campos já diz qual motor é.
      //
      // LEITURA: `dev_mem` é arredondado para potência de 2 e SATURA EM 8 (é
      // anti-fingerprinting, não precisão) — compare DISTRIBUIÇÃO contra a
      // frota, nunca média. `heap_max`/`heap_uso` são não-padrão e faltam em
      // WebKit e em parte dos WebView: ausente é NÃO MEDIDO, jamais zero. Ler
      // ausência como "heap pequeno" marcaria como pobre de memória justamente
      // quem não expõe o campo.
      dev_mem: 'num',
      heap_max: 'num',
      heap_uso: 'num',
      /**
       * EXECUÇÃO de `type=module`, que `es_ok` não mede (ele é sintaxe).
       *
       * Medido em 09/09/2026: no ramo dominante do boot morto os quatro módulos
       * externos chegam INTEIROS, `es_ok=2020` em 100%, nenhum `error` é
       * capturado, o documento fecha em `complete` — e nada executa. No mesmo
       * documento os 9 scripts clássicos inline rodam (é como `es_ok` é medido e
       * como o beacon sai). A única categoria que não roda é module, e todos os
       * nossos são externos.
       *
       *   mod_ok=true    o motor executa módulos ⇒ o problema é o carregamento
       *                  EXTERNO (host interceptando ou recusando)
       *   mod_ok ausente o motor não executa módulo NENHUM neste contexto
       *
       * As curas são opostas, e nenhum outro campo separa isso.
       */
      mod_ok: 'bool',
      /**
       * Violação de CSP — o `js_erro` NÃO cobre isto.
       *
       * Script bloqueado por Content-Security-Policy dispara
       * `securitypolicyviolation` no document, nunca `error` no elemento. Com o
       * quadro de 09-10/09 (módulo INLINE executa em 99,3%, os quatro EXTERNOS
       * baixam inteiros e não executam, zero `js_erro`), política é o que
       * sobrou — e a nossa origem não manda CSP nenhuma, então seria injetada
       * pelo app hospedeiro no WebView.
       *
       * Formato: "<diretiva> <arquivo>". `js_erro` vazio COM `csp` preenchido é
       * a assinatura; os dois vazios mantêm o mistério e derrubam esta
       * hipótese junto.
       */
      csp: 'raw',
      // Voltar do bfcache não cria entry de navegação nova — a entry congela na
      // carga original —, então `nav_type` e `bytes_rede` podem descrever uma
      // navegação de dias atrás. E `back_forward` NÃO é documento antigo:
      // navegação de histórico fora do bfcache re-parseia do cache HTTP com
      // `timeOrigin` novo (31% dos devices, `bytes_rede=0` em build viva).
      // `0` = observei e não houve; ausente = não observei; >=1 = os campos de
      // navegação descrevem outra carga. PAREADO com o campo homônimo do
      // pipeline de ChunkLoadError.
      doc_restaurado: 'num',
      // `readyState=complete` com módulos não avaliados está FORA da spec (defer
      // roda antes do DOMContentLoaded). Ou o WebView marca complete sem avaliar
      // módulo, ou o documento medido não é o que deveria tê-los rodado —
      // restaurado com o DOM montado. `idade_s` é a idade pelo relógio de
      // parede; `susp_ms` é o quanto o relógio monotônico ficou para trás
      // (documento congelado pelo host). Em documento vivo `susp_ms` é ausente.
      idade_s: 'num',
      susp_ms: 'num',
      display_mode: 'raw',
      nav_type: 'raw',
      // Sem `client_code_version` de propósito: ela vive no bundle que não
      // executou. A ausência é informação, não esquecimento.
      // QUANTO do bundle chegou — o discriminador entre as hipóteses vivas da
      // tela branca. `bytes` é `decodedBodySize` (depois de descomprimir, então
      // independe de brotli/gzip) e `bytes_rede` é `transferSize`. O tamanho
      // ESPERADO não vem daqui: o hash está no `main_pedido` e o tamanho por
      // hash se sabe do lado do servidor — o cruzamento é no Athena.
      //   sem entry / bytes 0        nem pedido / nada chegou
      //   bytes ~44722               veio o index.html no lugar do JS
      //   0 < bytes < esperado       ENTREGA TRUNCADA
      //   bytes = esperado, rede 0   veio do cache/service worker
      //   bytes = esperado, rede > 0 chegou inteiro e não executou
      bytes: 'num',
      bytes_rede: 'num',
      bytes_cod: 'num',
      // Cobertura do próprio instrumento: `ok` | `sem_api` | `sem_entry` |
      // `sem_tamanho` | `erro`. Sem este campo, ausência de dado seria lida como
      // ausência de defeito — e a população suspeita é iOS antigo, exatamente
      // onde Resource Timing Level 2 pode não existir.
      rt: 'raw',
      // O browser reclamou do bundle? Um module script truncado dá SyntaxError e
      // dispara `error` no elemento; a hipótese registrada fala em `load`
      // silencioso. Só medindo dá para saber qual dos dois é. Sozinho o tamanho
      // não separa truncamento de MIME errado — `js_erro` + `bytes` separam.
      // Estado do DOCUMENTO no instante da emissão. `estado` + `n_scripts` são
      // o PAR que separa "morreu durante o parse" de "documento inteiro":
      // `readyState` sozinho não decide, porque num teardown ele já apareceu
      // 'complete' com o parser parado. Existem porque as tags do bundle são
      // injetadas DEPOIS deste bloco (~277 bytes), então `main_pedido` vazio é
      // esperado e sem estes campos não dá para saber se foi por isso.
      estado: 'raw',
      n_scripts: 'num',
      // Um documento que ninguém olhou não é alguém desistindo. `vis`,
      // `prerender` e `topo` separam dano de ruído — sem eles, aba de fundo,
      // página pré-renderizada e iframe entram na mesma contagem que gente.
      vis: 'raw',
      prerender: 'bool',
      topo: 'num',
      // bfcache: o documento foi congelado, não morreu. Marca o evento em vez de
      // calá-lo, senão o documento que entra em bfcache e nunca volta some.
      persistido: 'bool',
      // O relógio CERTO para "há quanto tempo essa pessoa está na tela branca".
      // O `ms` conta do fim do parse deste bloco e já descontou DNS/TLS/TTFB.
      ms_nav: 'num',
      js_erro: 'raw',
      // O CANÁRIO DE SINTAXE (primeiro `<script>` do `<head>`, ES3 puro):
      // `2020` se este motor PARSEIA ES2020, `0` se não. É o campo que troca
      // por MEDIÇÃO a inferência de que os motores antigos recusam o bundle: há
      // 391 `?.` no `main.js`, o primeiro no byte 855 de 589906, e nos 4
      // eventos com bytes medidos o arquivo chegou INTEIRO (tamanho exato) em
      // motores Chromium 42/44/56/63.
      //
      // AUSENTE = o canário não rodou (documento fóssil anterior a 01/09/2026).
      // `0` é valor legítimo — nunca filtrar por verdadeiro nesta coluna.
      es_ok: 'num',
      // A RECUPERAÇÃO AUTOMÁTICA da sonda (limpa caches + desregistra service
      // workers + recarrega UMA vez por release).
      //
      // `recup_motivo` é o que a sonda DECIDIU: 'disparou' | 'ja_tentou' |
      // 'es_antigo' | 'oculto' | 'sem_trava' | 'desligado'. A eficácia da cura
      // se lê pela RAZÃO 'disparou' : 'ja_tentou' — se o reload cura,
      // 'ja_tentou' tende a zero. `recuperou` é o recorte grosso (verdadeiro em
      // 'disparou' e 'ja_tentou'), para separar rápido a população que já
      // tentou se curar sozinha.
      //
      // Vigilância operacional: mais de 2-3 'disparou' no mesmo `device_id` por
      // dia é LOOP de reload (trava de `sessionStorage` que aceita `setItem` e
      // não persiste), não recuperação.
      recuperou: 'bool',
      recup_motivo: 'raw',
      /**
       * QUAL dos quatro arquivos iniciais faltou.
       *
       * Os quatro (`runtime`/`polyfills`/`vendor`/`main`) são módulos
       * independentes ligados pelo array `self.webpackChunkapp`, e a última
       * linha do `main.js` é `re.O(0,["vendor"],()=>re(re.s=996))`: o módulo de
       * entrada só roda DEPOIS que o chunk `vendor` registrar. Vendor ausente ⇒
       * main baixa inteiro, executa o próprio push, e a callback nunca drena —
       * sem exceção, sem `js_erro`, com `readyState=complete`. Era o quadro dos
       * 852 devices/2h de 08/09/2026, e `bytes` (só do main) não o separava de
       * nada.
       *
       * `wp` só existe quando NÃO houve array ('sem-array' | 'erro'); com array,
       * valem os campos abaixo. `wp_runtime=false` significa que o próprio
       * runtime.js não executou.
       */
      wp: 'raw',
      wp_runtime: 'bool',
      wp_n: 'num',
      wp_vendor: 'bool',
      wp_main: 'bool',
      wp_polyfills: 'bool',
      // decodedBodySize de CADA um dos quatro. Publicados hoje: runtime 10.316
      // · polyfills 34.954 · vendor 785.081 · main 613.104 — o vendor é o maior
      // e é o único de que o main depende.
      //
      // `0` é AFIRMATIVO (pedido, nada chegou). `-1` (sem entry) é AMBÍGUO:
      // cabem "ainda baixando quando o documento morreu" e, em WebKit, "a carga
      // falhou" — o Safari não cria entry para recurso que não completa. Como
      // 80,5% desta coorte é webview (e no iOS webview é WebKit), NÃO ler `-1`
      // como "não foi pedido".
      //
      // Idem para `wp_n`: num boot morto o esperado é 2-3 (os lazy nem chegam a
      // ser pedidos), contra ~20 num boot saudável. A contagem baixa é normal
      // aqui e NÃO distingue causa — quem decide é o par wp_vendor/wp_main.
      b_runtime: 'num',
      b_polyfills: 'num',
      b_vendor: 'num',
      b_main: 'num',
    },
  },
  /**
   * Quem desistiu ANTES do prazo da sonda — a população que o passo acima não
   * enxerga.
   *
   * Mesma sonda inline, mesmos campos, disparado no `pagehide` em vez do
   * timer. Existe porque `PRAZO_MS` (25s) não é folga e sim precedência sobre o
   * vigia (20s), então baixá-lo não é opção — mas 25s é mais do que a paciência
   * de quem encara uma tela branca. MEDIDO em 27/08/2026: `levapizza` produziu
   * seis reloads em 133s, um a cada ~22s, e tem ZERO `ClientBootNoScript`.
   *
   * Step separado de propósito: `ClientBootNoScript` segue sendo a série
   * histórica comparável desde 15/08. A RAZÃO entre os dois é que quantifica o
   * viés — e o `ms` daqui é o tempo real até a desistência, não constante, o
   * que dá a distribuição de paciência.
   *
   * Um documento emite UM dos dois, nunca ambos: quem chegar primeiro fecha.
   */
  ClientBootAbandoned: {
    out: {
      slug: 'raw',
      user_agent: 'raw',
      main_pedido: 'raw',
      // Aqui `ms` é MEDIDO (tempo até o abandono), não a constante do prazo.
      ms: 'num',
      doc_from_sw: 'bool',
      sw_controlled: 'bool',
      net_type: 'raw',
      // Memória, testando a hipótese que sobra depois que rede e sintaxe
      // caíram: 1,44 MB de JS para compilar de uma vez, e webview embarcado tem
      // teto menor que o browser nativo do mesmo aparelho. `dev_mem` é o RAM do
      // APARELHO em GB (Chromium arredonda, teto 8); `heap_max`/`heap_uso` são o
      // teto e o uso de heap DESTE documento. Só existem em Chromium — e a
      // coorte de 08/09/2026 é 100% Android/Chromium, zero WKWebView; em iOS a
      // ausência dos campos já diz qual motor é.
      //
      // LEITURA: `dev_mem` é arredondado para potência de 2 e SATURA EM 8 (é
      // anti-fingerprinting, não precisão) — compare DISTRIBUIÇÃO contra a
      // frota, nunca média. `heap_max`/`heap_uso` são não-padrão e faltam em
      // WebKit e em parte dos WebView: ausente é NÃO MEDIDO, jamais zero. Ler
      // ausência como "heap pequeno" marcaria como pobre de memória justamente
      // quem não expõe o campo.
      dev_mem: 'num',
      heap_max: 'num',
      heap_uso: 'num',
      /**
       * EXECUÇÃO de `type=module`, que `es_ok` não mede (ele é sintaxe).
       *
       * Medido em 09/09/2026: no ramo dominante do boot morto os quatro módulos
       * externos chegam INTEIROS, `es_ok=2020` em 100%, nenhum `error` é
       * capturado, o documento fecha em `complete` — e nada executa. No mesmo
       * documento os 9 scripts clássicos inline rodam (é como `es_ok` é medido e
       * como o beacon sai). A única categoria que não roda é module, e todos os
       * nossos são externos.
       *
       *   mod_ok=true    o motor executa módulos ⇒ o problema é o carregamento
       *                  EXTERNO (host interceptando ou recusando)
       *   mod_ok ausente o motor não executa módulo NENHUM neste contexto
       *
       * As curas são opostas, e nenhum outro campo separa isso.
       */
      mod_ok: 'bool',
      /**
       * Violação de CSP — o `js_erro` NÃO cobre isto.
       *
       * Script bloqueado por Content-Security-Policy dispara
       * `securitypolicyviolation` no document, nunca `error` no elemento. Com o
       * quadro de 09-10/09 (módulo INLINE executa em 99,3%, os quatro EXTERNOS
       * baixam inteiros e não executam, zero `js_erro`), política é o que
       * sobrou — e a nossa origem não manda CSP nenhuma, então seria injetada
       * pelo app hospedeiro no WebView.
       *
       * Formato: "<diretiva> <arquivo>". `js_erro` vazio COM `csp` preenchido é
       * a assinatura; os dois vazios mantêm o mistério e derrubam esta
       * hipótese junto.
       */
      csp: 'raw',
      // Voltar do bfcache não cria entry de navegação nova — a entry congela na
      // carga original —, então `nav_type` e `bytes_rede` podem descrever uma
      // navegação de dias atrás. E `back_forward` NÃO é documento antigo:
      // navegação de histórico fora do bfcache re-parseia do cache HTTP com
      // `timeOrigin` novo (31% dos devices, `bytes_rede=0` em build viva).
      // `0` = observei e não houve; ausente = não observei; >=1 = os campos de
      // navegação descrevem outra carga. PAREADO com o campo homônimo do
      // pipeline de ChunkLoadError.
      doc_restaurado: 'num',
      // `readyState=complete` com módulos não avaliados está FORA da spec (defer
      // roda antes do DOMContentLoaded). Ou o WebView marca complete sem avaliar
      // módulo, ou o documento medido não é o que deveria tê-los rodado —
      // restaurado com o DOM montado. `idade_s` é a idade pelo relógio de
      // parede; `susp_ms` é o quanto o relógio monotônico ficou para trás
      // (documento congelado pelo host). Em documento vivo `susp_ms` é ausente.
      idade_s: 'num',
      susp_ms: 'num',
      display_mode: 'raw',
      nav_type: 'raw',
      // QUANTO do bundle chegou — o discriminador entre as hipóteses vivas da
      // tela branca. `bytes` é `decodedBodySize` (depois de descomprimir, então
      // independe de brotli/gzip) e `bytes_rede` é `transferSize`. O tamanho
      // ESPERADO não vem daqui: o hash está no `main_pedido` e o tamanho por
      // hash se sabe do lado do servidor — o cruzamento é no Athena.
      //   sem entry / bytes 0        nem pedido / nada chegou
      //   bytes ~44722               veio o index.html no lugar do JS
      //   0 < bytes < esperado       ENTREGA TRUNCADA
      //   bytes = esperado, rede 0   veio do cache/service worker
      //   bytes = esperado, rede > 0 chegou inteiro e não executou
      bytes: 'num',
      bytes_rede: 'num',
      bytes_cod: 'num',
      // Cobertura do próprio instrumento: `ok` | `sem_api` | `sem_entry` |
      // `sem_tamanho` | `erro`. Sem este campo, ausência de dado seria lida como
      // ausência de defeito — e a população suspeita é iOS antigo, exatamente
      // onde Resource Timing Level 2 pode não existir.
      rt: 'raw',
      // O browser reclamou do bundle? Um module script truncado dá SyntaxError e
      // dispara `error` no elemento; a hipótese registrada fala em `load`
      // silencioso. Só medindo dá para saber qual dos dois é. Sozinho o tamanho
      // não separa truncamento de MIME errado — `js_erro` + `bytes` separam.
      // Estado do DOCUMENTO no instante da emissão. `estado` + `n_scripts` são
      // o PAR que separa "morreu durante o parse" de "documento inteiro":
      // `readyState` sozinho não decide, porque num teardown ele já apareceu
      // 'complete' com o parser parado. Existem porque as tags do bundle são
      // injetadas DEPOIS deste bloco (~277 bytes), então `main_pedido` vazio é
      // esperado e sem estes campos não dá para saber se foi por isso.
      estado: 'raw',
      n_scripts: 'num',
      // Um documento que ninguém olhou não é alguém desistindo. `vis`,
      // `prerender` e `topo` separam dano de ruído — sem eles, aba de fundo,
      // página pré-renderizada e iframe entram na mesma contagem que gente.
      vis: 'raw',
      prerender: 'bool',
      topo: 'num',
      // bfcache: o documento foi congelado, não morreu. Marca o evento em vez de
      // calá-lo, senão o documento que entra em bfcache e nunca volta some.
      persistido: 'bool',
      // O relógio CERTO para "há quanto tempo essa pessoa está na tela branca".
      // O `ms` conta do fim do parse deste bloco e já descontou DNS/TLS/TTFB.
      ms_nav: 'num',
      js_erro: 'raw',
      // Mesmos campos do passo do prazo — a população que desiste antes de 25 s
      // NÃO pode ficar cega justamente na coluna nova. Ver `ClientBootNoScript`
      // acima para o que cada um significa.
      //
      // Diferença de leitura aqui: o abandono NUNCA dispara recuperação (a
      // página já está saindo), então `recup_motivo` só assume '' ou
      // 'ja_tentou' — é leitura PURA da trava. E `recuperou = true` neste passo
      // é evidência direta de que o device já recarregou automaticamente nesta
      // release e mesmo assim desistiu: a recuperação não atende àquela causa.
      es_ok: 'num',
      recuperou: 'bool',
      recup_motivo: 'raw',
      /**
       * QUAL dos quatro arquivos iniciais faltou.
       *
       * Os quatro (`runtime`/`polyfills`/`vendor`/`main`) são módulos
       * independentes ligados pelo array `self.webpackChunkapp`, e a última
       * linha do `main.js` é `re.O(0,["vendor"],()=>re(re.s=996))`: o módulo de
       * entrada só roda DEPOIS que o chunk `vendor` registrar. Vendor ausente ⇒
       * main baixa inteiro, executa o próprio push, e a callback nunca drena —
       * sem exceção, sem `js_erro`, com `readyState=complete`. Era o quadro dos
       * 852 devices/2h de 08/09/2026, e `bytes` (só do main) não o separava de
       * nada.
       *
       * `wp` só existe quando NÃO houve array ('sem-array' | 'erro'); com array,
       * valem os campos abaixo. `wp_runtime=false` significa que o próprio
       * runtime.js não executou.
       */
      wp: 'raw',
      wp_runtime: 'bool',
      wp_n: 'num',
      wp_vendor: 'bool',
      wp_main: 'bool',
      wp_polyfills: 'bool',
      // decodedBodySize de CADA um dos quatro. `-1` = sem entry no Resource
      // Timing (não pedido, ou ainda baixando), que é diferente de `0` (pedido
      // e nada chegou). Publicados hoje: runtime 10.316 · polyfills 34.954 ·
      // vendor 785.081 · main 613.104 — o vendor é o maior e é o único de que
      // o main depende.
      b_runtime: 'num',
      b_polyfills: 'num',
      b_vendor: 'num',
      b_main: 'num',
    },
  },
  /**
   * O documento carregou SEM o slug da loja no caminho (`/app/` puro), e este
   * passo diz DE ONDE ele veio. Emitido pela sonda inline do `index.html`,
   * então — como `ClientBootAbandoned` e `ClientBootStalled` — ele NÃO passa
   * pela allowlist daqui: a declaração abaixo é o contrato lido, não o filtro
   * aplicado. Mexer num nome aqui não muda o que sai do browser; muda no
   * `index.html`, e os dois precisam andar juntos.
   *
   * Um documento sem slug tem desfecho único: `startup.service.ts` não acha o
   * primeiro segmento, o app pede `/restaurant/data/v2/undefined`, leva 404, e
   * o `menu.page` mostra "Loja não encontrada! Verifique a URL" — com o link do
   * lojista ainda correto. MEDIDO em 06-08/09/2026: 1.103 devices/3 dias nessa
   * situação, 380 deles (~127/dia) COM uma loja carregada antes, 92% em
   * documento novo (`correlation_id` diferente) e 46% logo depois de um
   * `ClientOrderSubmitted`. O motivo da reabertura sem slug é o que falta, e é
   * o que os campos abaixo respondem. A justificativa longa de cada leitura,
   * e as quatro hipóteses já refutadas, estão no `index.html` — não duplicar.
   */
  ClientBootNoSlug: {
    out: {
      // Caminho em que o documento subiu. Esperado `/app/`; qualquer outra
      // coisa (ex.: `/index.html`, que a raiz do domínio também serve) é achado
      // por si só, e por isso o campo não é constante.
      entrada: 'raw',
      // 'nenhum' | 'proprio' | 'externo' | 'erro'. Com 'proprio', `ref` traz o
      // CAMINHO de onde saímos (rota do app, sem query) e é a prova direta de
      // navegação interna; com 'externo', só o HOST — o caminho de terceiro
      // pode carregar dado de quem navega e não entra.
      ref_tipo: 'raw',
      ref: 'raw',
      // NOMES dos parâmetros da query, nunca os valores: `cid,oid` é retorno de
      // pagamento, `u` é link de recuperação de carrinho, `t` é mesa. É o campo
      // que atribui o documento a um fluxo sem carregar identificador de ninguém.
      query_keys: 'raw',
      // A âncora: última rota COM slug que ESTE device viu, e há quanto tempo.
      // Separa "nunca teve slug" de "tinha e perdeu" sem depender do IP, que
      // muda entre sessões e por isso não serviu na investigação.
      ultima_rota: 'raw',
      ha_ms: 'num',
      // 'navigate' | 'reload' | 'back_forward'. Com `ref_tipo=nenhum`, um
      // back_forward/reload é restauração de aba — a hipótese que abriu isto.
      nav_type: 'raw',
      // 'standalone' distingue atalho na tela inicial / PWA instalado de aba
      // comum: um WebAPK abre no `start_url` gravado na INSTALAÇÃO, que pode ser
      // anterior ao manifest por loja.
      display_mode: 'raw',
      sw_controlled: 'bool',
      doc_from_sw: 'bool',
      net_type: 'raw',
      es_ok: 'num',
      // Documento que ninguém olhou não é consumidor perdido: `vis`, `topo` e
      // `prerender` separam dano de ruído, como no `ClientBootAbandoned`.
      vis: 'raw',
      topo: 'num',
      prerender: 'bool',
      user_agent: 'raw',
    },
  },
  ClientApiCallFailed: {
    out: {
      // Família estável do endpoint (order_submit|delivery_fee|coupon|menu|
      // payment_status) — nunca a URL crua (query string pode carregar dado).
      endpoint_family: 'raw',
      method: 'raw',
      status: 'num',
      ms: 'num',
      timeout: 'bool',
    },
  },
};

/** Nomes válidos de step — usado pelo guard de convenção no spec. */
export const ORDER_FLOW_STEP_NAMES = Object.keys(ORDER_FLOW_STEPS);
